import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import _, { orderBy } from "lodash";
import moment from "moment-timezone";
import { maskPatientFullName } from "../../../utils/maskPatientName";
import Button from "../../Shared/Button";
import SecureFileLinkPicker from "./SecureFileLinkPicker";

import { sendEmailService } from "../../../services/workflow";
import Select, { components } from "react-select";
import { getAllEmailTemplates } from "../../../services/emailTemplates";
import { getTimezone } from "../utils";
import { APPOINTMENT_TYPE, DEFAULT_TIMEZONE } from "../../../constants";
import { insertTextAtSelection } from "../../../utils/insertTextAtSelection";
import { getToggleEnabled, TOGGLES } from "../../../toggles";

import { displayTimestamp } from "../../../utils/dateDisplay";
const Input = (props) => {
  return <components.Input {...props} />;
};

const customSelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    height: 30,
    minHeight: 30,
  }),
};

const EmailSection = ({ ca }) => {
  const isSecureFilesEnabled = getToggleEnabled(TOGGLES.SECURE_FILES);
  const practitionerPreferences = useSelector(({ app }) => app?.practitionerPreferences);
  const specialistProfiles = useSelector((state) => state.app.specialistProfiles);
  const [selectedFile, setSelectedFile] = useState(null);
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [inAction, setInAction] = useState(false);
  const manualSentLogs = _.get(ca, "manual_email.sent", []);
  const [emailAddress, setEmailAddress] = useState();
  const defaultEmailAddressList = [
    { label: `client: ${_.get(ca, "client.email", undefined)}`, value: _.get(ca, "client.email", undefined) },
    { label: `clinic: ${_.get(ca, "clinic.email", undefined)}`, value: _.get(ca, "clinic.email", undefined) },
    {
      label: `referrer: ${_.get(ca, "referral.referrerLocation.email", undefined)}`,
      value: _.get(ca, "referral.referrerLocation.email", undefined),
    },
    {
      label: `Case Manager: ${_.get(ca, "practitioner.email", undefined)}`,
      value: _.get(ca, "practitioner.email", undefined),
    },
  ].filter((emailAddress) => emailAddress.value);
  const [emailAddressList, setEmailAddressList] = useState(defaultEmailAddressList);
  const [defaultEmailTemplates, setDefaultEmailTemplates] = useState([]);
  const [selectedEmailTemplates, setSelectedEmailTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState("");
  const [isSecureFilePickerOpen, setIsSecureFilePickerOpen] = useState(false);
  const emailBodyRef = useRef(null);
  const savedSelectionRef = useRef({ start: 0, end: 0 });
  const referrerPostcode = _.get(ca, "referral.referrerLocation.postCode", "");
  const [referrerTimezone, setReferrerTimezone] = useState(DEFAULT_TIMEZONE);
  // Readiness is "the lookup for *this* postcode has answered", not a flag:
  // when a mounted section receives a new postcode, the first render still
  // sees the previous zone, and a flag reset in an effect arrives one render
  // too late to stop a send with the stale template.
  const [resolvedPostcode, setResolvedPostcode] = useState(null);
  // The zone the current subject/body were rendered with. Set by the template
  // effect, which runs one render after the lookup commits - so readiness also
  // waits for the fields to have been rebuilt, not just for the zone to exist.
  const [templateZone, setTemplateZone] = useState(null);
  const isReferrerTimezoneResolved = resolvedPostcode === referrerPostcode && templateZone === referrerTimezone;

  // Resolved before the template is rendered, not after. The previous version
  // kicked off the lookup and assigned its result inside a .then() that ran
  // long after the synchronous template build had used the initial value -
  // and then hardcoded Melbourne anyway, so the GP always got Melbourne time.
  useEffect(() => {
    let cancelled = false;
    getTimezone(referrerPostcode).then((timezone) => {
      if (cancelled) return;
      setReferrerTimezone(timezone || DEFAULT_TIMEZONE);
      setResolvedPostcode(referrerPostcode);
    });
    return () => {
      cancelled = true;
    };
  }, [referrerPostcode]);

  useEffect(() => {
    getAllEmailTemplates().then((response) => {
      const emailTemplates = response.result.filter((temp) => temp.type === "MANUAL_EMAIL");
      const emailTemplatesInOrder = orderBy(emailTemplates, [(template) => template.name.toLowerCase()], ["asc"]);
      setDefaultEmailTemplates(emailTemplatesInOrder);
      setSelectedEmailTemplates(emailTemplatesInOrder);
    });
  }, []);

  useEffect(() => {
    const selectedTemplate = defaultEmailTemplates.find((temp) => temp.id === currentTemplateId) || {
      subject: "",
      body: "",
    };
    const referrerLastName = _.get(ca, "referrer.lastName", "[Referrer Last Name]");
    const referrerFullName = `${_.get(ca, "referrer.firstName", "[Referrer First Name]")} ${referrerLastName}`;
    const patientFirstName = _.get(ca, "client.firstName", "[patientFirstName]");
    const patientLastName = _.get(ca, "client.lastName", "[patientLastName]");
    const patientFullName = `${patientFirstName} ${patientLastName}`;
    const patientFirstNameInitial = _.upperCase(_.head(_.trim(patientFirstName)));
    const patientLastNameInitial = _.upperCase(_.head(_.trim(patientLastName)));
    const patientFullNameMasked = maskPatientFullName(patientFullName);
    const specialistFistName = _.get(ca, "practitioner.firstName", "[Specialist First Name]");
    const specialistFullName = `${specialistFistName} ${_.get(ca, "practitioner.lastName", "[Specialist First Name]")}`;
    const practitionerId = _.get(ca, "practitioner.practitionerId", "");
    const practitionerPreference = practitionerPreferences.find(
      (practitionerPreference) => practitionerPreference.practitionerId === practitionerId
    );
    const practitionerVideoLink = practitionerPreference?.videoLink ?? "";
    const practitionerVideoId = practitionerPreference?.videoId ?? "";
    const patientDOB = _.get(ca, "client.dateOfBirth", "[Patient DOB]");
    const clinicPhoneNumber = _.get(ca, "clinic.phone", "[Clinic Phone Number]");
    const patientPhoneNumber = _.get(
      ca,
      "client.phoneNumberMobile",
      _.get(ca, "client.phoneNumberHome", "[Patient Phone Number]")
    );
    const clinicName = _.get(ca, "clinic.name", "[Clinic Name]");
    // Explicit format in the tenant zone: toLocaleDateString() followed the
    // coordinator's OS locale, so a US-English machine put 9/12/2026 in front
    // of a GP who reads that as 9 December.
    const currentDate = moment().tz(DEFAULT_TIMEZONE).format("DD/MM/YYYY");
    const caSpecialties = _.get(ca, "practitioner.specialties", []);
    const profileSpecialties = specialistProfiles.find(({ id }) => id === practitionerId)?.specialties ?? [];
    const specialtySource = !_.isEmpty(caSpecialties) ? caSpecialties : profileSpecialties;
    const practitionerSpecialties = specialtySource
      .slice(0, 3)
      .map((s) => (typeof s === "string" ? s : s?.name))
      .filter(Boolean)
      .join(", ");
    const customFields = _.get(ca, "appointment.customFields", []);
    const appointmentType = _.get(ca, "appointment.appointmentType", "");
    const field = _.find(customFields, (f) => f.name === "VCURL");
    const videoLink = appointmentType === APPOINTMENT_TYPE.F2F ? null : _.get(field, "value", null);
    let room = "[Room]";
    if (videoLink) {
      try {
        const videoLinkUrl = new URL(videoLink);
        room = videoLinkUrl.searchParams.get("room");
      } catch (error) {
        console.error(error.message);
      }
    }
    const appointmentDate = moment
      .tz(_.get(ca, "appointment.startDateTime", ""), "YYYY-MM-DDTHH:mm:ss Z", referrerTimezone)
      .tz(referrerTimezone)
      .format("DD-MM-YYYY h:mmA z");
    const patientBookingLink = `${process.env.REACT_APP_PATIENT_PORTAL_BASE_URL}/self-service/${ca.id}?smsTemplateId=ff8ea743-4dd4-40af-b99e-4063a6919dcb`;
    const resolvePlaceholders = (text) =>
      text
        .replaceAll("[Referrer Full Name]", referrerFullName)
        .replaceAll("[Clinic Phone Number]", clinicPhoneNumber)
        .replaceAll("[Clinic Name]", clinicName)
        .replaceAll("[Current Date]", currentDate)
        .replaceAll("[Referrer Name]", referrerLastName)
        .replaceAll("[Patient Name]", patientFullName)
        .replaceAll("[patientFullName]", patientFullName)
        .replaceAll("[patientFirstName]", patientFirstName)
        .replaceAll("[patientLastName]", patientLastName)
        .replaceAll("[patientFirstNameInitial]", patientFirstNameInitial)
        .replaceAll("[patientLastNameInitial]", patientLastNameInitial)
        .replaceAll("[patientFullNameMasked]", patientFullNameMasked)
        .replaceAll("[Patient DOB]", patientDOB)
        .replaceAll("[Patient Phone Number]", patientPhoneNumber)
        .replaceAll("[Specialist Full Name]", specialistFullName)
        .replaceAll("[Specialist First Name]", specialistFistName)
        .replaceAll("[Specialty]", practitionerSpecialties)
        .replaceAll("[Booked Date Time]", appointmentDate)
        .replaceAll("[Timezone]", referrerTimezone)
        .replaceAll("[Practitioner Video Link]", practitionerVideoLink)
        .replaceAll("[Practitioner Video ID]", practitionerVideoId)
        .replaceAll("[Room]", room)
        .replaceAll("[Patient Booking Link]", patientBookingLink);
    setSubject(resolvePlaceholders(selectedTemplate.subject));
    setEmailBody(resolvePlaceholders(selectedTemplate.body));
    setTemplateZone(referrerTimezone);
  }, [currentTemplateId, defaultEmailTemplates, specialistProfiles, referrerTimezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateEmailBodySelection = () => {
    const textarea = emailBodyRef.current;
    if (!textarea) {
      return;
    }
    savedSelectionRef.current = {
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
    };
  };

  const openSecureFilePicker = () => {
    updateEmailBodySelection();
    setIsSecureFilePickerOpen(true);
  };

  const handleInsertSecureFileLinks = (linkText) => {
    if (!linkText) {
      return;
    }
    const { start, end } = savedSelectionRef.current;
    const { nextValue, nextSelection } = insertTextAtSelection({
      text: linkText,
      value: emailBody,
      selectionStart: start,
      selectionEnd: end,
    });
    setEmailBody(nextValue);
    savedSelectionRef.current = { start: nextSelection, end: nextSelection };
    requestAnimationFrame(() => {
      const textarea = emailBodyRef.current;
      if (!textarea) {
        return;
      }
      textarea.focus();
      textarea.setSelectionRange(nextSelection, nextSelection);
    });
  };

  const toBase64 = (file) => {
    return new Promise((resolve) => {
      let baseURL = "";
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        baseURL = reader.result;
        resolve(baseURL);
      };
    });
  };

  const handleInputChange = (characterEntered) => {
    if (characterEntered === "") return;
    setEmailAddressList([{ label: characterEntered, value: characterEntered }, ...defaultEmailAddressList]);
  };

  const handleTemplateInputchange = (characterEntered) => {
    if (characterEntered === "") return;
    setSelectedEmailTemplates([{ label: characterEntered, value: characterEntered }, ...defaultEmailTemplates]);
  };

  const handleSubmit = async () => {
    setInAction(true);

    const data = {
      to: emailAddress,
      subject: subject,
      content: emailBody,
      id: ca.id,
    };
    if (selectedFile) {
      const fileBase64 = await toBase64(selectedFile);
      const fileContent = _.split(fileBase64, "base64,");

      const attachment = {
        content: fileContent[1],
        filename: selectedFile.name,
        type: selectedFile.type,
        disposition: "attachment",
      };
      data.attachments = [attachment];
    }
    try {
      await sendEmailService(data);
      alert("Send email success!");
      setSubject("");
      setEmailBody("");
    } catch (error) {
      const reason = _.get(error, "response.data.message", "");
      alert(reason ? `Send email fail!\n\n${reason}` : "Send email fail!");
    } finally {
      setInAction(false);
    }
  };

  return (
    <div>
      <div className="columns is-radiusless is-4 workflow-email-section">
        <div className="column box email-content is-radiusless is-shadowless">
          <div className="message is-small has-background-white">
            <div className="bottom-gap">
              <p className="menu-label">Email Address</p>
              <Select
                className="has-text-left is-radiusless is-size-7"
                components={{ Input }}
                onInputChange={handleInputChange}
                onChange={(selectOption) => setEmailAddress(selectOption.value)}
                options={emailAddressList}
                placeholder="please input email address"
                styles={customSelectStyles}
              />
            </div>
            <div className="bottom-gap">
              <p className="menu-label">Email Templates</p>
              <Select
                className="has-text-left is-radiusless is-size-7"
                components={{ Input }}
                onInputChange={handleTemplateInputchange}
                onChange={(target) => setCurrentTemplateId(target.value)}
                options={selectedEmailTemplates.map(({ id, name }) => ({ label: name, value: id }))}
                placeholder="please input email template"
                styles={customSelectStyles}
                isDisabled={!emailAddress}
              />
            </div>
            <div className="bottom-gap">
              <p className="menu-label">Email subject</p>
              <input
                className="input is-radiusless is-size-7"
                onChange={(e) => setSubject(e.target.value)}
                value={subject}
                disabled={!emailAddress}
                placeholder="please input email subject"
              />
            </div>
            <div>
              <div className="is-flex is-align-items-center is-justify-content-space-between mb-2">
                <p className="menu-label mb-0">Email body</p>
                {isSecureFilesEnabled && (
                  <button
                    type="button"
                    className="button is-small is-info is-light is-radiusless"
                    onClick={openSecureFilePicker}
                    disabled={!emailAddress}
                  >
                    Insert Secure File Link
                  </button>
                )}
              </div>
              <textarea
                ref={emailBodyRef}
                rows={3}
                className="textarea is-radiusless is-size-7"
                onChange={(e) => setEmailBody(e.target.value)}
                onSelect={updateEmailBodySelection}
                onKeyUp={updateEmailBodySelection}
                onClick={updateEmailBodySelection}
                value={emailBody}
                disabled={!emailAddress}
                placeholder="please input email content"
              />
            </div>
          </div>
          <div className="bottom-gap">
            <p className="menu-label">Upload attachment</p>
            <input
              type="file"
              name="file"
              className="is-radiusless upload-button is-size-7"
              disabled={!emailAddress}
              onChange={(e) => {
                setSelectedFile(e.target.files[0]);
              }}
            />
          </div>
          <Button
            text="SEND EMAIL"
            onClick={handleSubmit}
            isLoading={inAction || !isReferrerTimezoneResolved}
            disabled={!subject || !emailAddress || !isReferrerTimezoneResolved}
          />
          {isSecureFilesEnabled && (
            <SecureFileLinkPicker
              isOpen={isSecureFilePickerOpen}
              onClose={() => setIsSecureFilePickerOpen(false)}
              clientAppointmentId={ca.id}
              onInsertLinks={handleInsertSecureFileLinks}
            />
          )}
        </div>
        <div className="column box is-small email-content is-radiusless is-shadowless">
          <div>Manual Email</div>
          {manualSentLogs.map((emailLog, idx) => (
            <div key={idx}>
              [{displayTimestamp(emailLog.time)}] {emailLog.msg}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmailSection;
